import React, { useEffect, useState } from "react";
import {
    FormRenderer as DDFFormRenderer, componentTypes as iComponentTypes,
    useFieldApi, useFormApi, validatorTypes
} from "@data-driven-forms/react-form-renderer";
import { Button } from "react-bootstrap";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Icon from "./Icon";
import { humanReadableFileSize } from "../../utils/common";
import DatePicker from "react-multi-date-picker";
import ReactQuill, { Quill } from "react-quill";
import { EDITOR_CONFIG, EVIDENCE_TYPE } from "../../utils/constants";

export const componentTypes = {
    TEXT_FIELD: iComponentTypes.TEXT_FIELD,
    DATE_PICKER: iComponentTypes.DATE_PICKER,
    TEXTAREA: iComponentTypes.TEXTAREA,
    SELECT: iComponentTypes.SELECT,
    PLAIN_TEXT: iComponentTypes.PLAIN_TEXT,
    CHECKBOX: iComponentTypes.CHECKBOX,
    WIZARD: iComponentTypes.WIZARD,
    TAB_ITEM: iComponentTypes.TAB_ITEM,
    FILE_UPLOAD: 'file-upload',
    DOCUMENTS_UPLOAD: 'documents-upload',
    DOCUMENTS_UPLOAD_NEW: 'documents-upload-new',
    ASYNC_SELECT: 'async-select',
    MONTH_PICKER: 'month-picker',
    INPUT_AS_TEXT: 'input-as-text',
    TEXT_EDITOR: 'text-editor',
    HTML: 'html'
}

const editorConfig = EDITOR_CONFIG;
const Block = Quill.import('blots/block');
Block.tagName = 'DIV';
Quill.register(Block, true);

function fileSizeValidator({ maxSize }: any) {
    return (value: any) => {
        if (value && value.inputFiles[0] && value.inputFiles[0].size > maxSize) {
            return `Maximum allowed size is ${humanReadableFileSize(maxSize)}.`;
        }
    }
}
function fileTypeValidator({ regex }: any) {
    return (value: any) => {
        if (value && value.inputFiles) {
            const file = value.inputFiles[0];
            if (file && !regex.exec(file.name)) {
                return `Invalid file extension.`
            }
        }
    }
}

function TextField(props: any) {
    const { label, meta = {}, input, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    const maxLength = ((props.validate || []).find((x: any) => x.type === validatorTypes.MAX_LENGTH) || {}).threshold || 255;
    const isPasswordField = props.fieldType === 'password';
    const [showPassword, setShowPassword] = useState(false);

    function onInput(input: any) {
        return {
            ...input,
            type: isPasswordField ? (showPassword ? 'text' : 'password') : (props.fieldType || 'text'),
            required,
            placeholder: label ? (props.placeholder || `Enter ${label}`) : undefined,
            onChange: (e: any) => {
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            onPaste: (e: any) => {
                if (props.onPaste) {
                    props.onPaste(e);
                }
            },
            onKeyDown: (e: any) => {
                if (props.fieldType === 'number') {
                    const key = e.charCode || e.keyCode || 0;
                    const value = e.target.value;
                    const keys = [8, 9, 13, 35, 36, 37, 39, 46, 110, 190];
                    if (keys.includes(key) || (key >= 48 && key <= 57) || (key >= 96 && key <= 105)) {
                        if (((key >= 48 && key <= 57) || (key >= 96 && key <= 105)) && `${value}`.length >= maxLength) {
                            e.preventDefault();
                        }
                    } else {
                        e.preventDefault();
                    }
                }
            }
        }
    }

    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            }
            <div className={`input-group ${isPasswordField ? 'has-group-text-right' : ''}`}>
                <input id={name}
                    className={`form-control ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''} ${props.styleClass || ''}`}
                    {...onInput(input)} maxLength={maxLength || 255} />
                {
                    isPasswordField &&
                    <span className="text-black-600 input-group-text">
                        <Icon name={showPassword ? 'eye-slash' : 'eye'} text={showPassword ? 'Hide' : 'Show'}
                            action={() => setShowPassword(!showPassword)} />
                    </span>
                }
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    )
}

function TextAreaField(props: any) {
    const { label, meta = {}, input, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    const maxLength = ((props.validate || []).find((x: any) => x.type === validatorTypes.MAX_LENGTH) || {}).threshold || 255;

    function onInput(input: any) {
        return {
            ...input,
            required,
            placeholder: props.placeholder || `Enter ${label}`,
            disabled: props.disabled,
            onChange: (e: any) => {
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            }
        }
    }

    return (
        <div className={`form-group ${props.className || ''}`}>
            <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            <div className="input-group">
                <textarea id={name} className={`form-control ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''}`}
                    maxLength={maxLength} {...onInput(input)} rows={props.rows || 3} />
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    )
}

function InputasTextAreaField(props: any) {
    const { label, meta = {}, input, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    const maxLength = ((props.validate || []).find((x: any) => x.type === validatorTypes.MAX_LENGTH) || {}).threshold || 255;

    function onInput(input: any) {
        return {
            ...input,
            required,
            placeholder: props.placeholder || `Enter ${label}`,
            onChange: (e: any) => {
                const oldVal = e.target.value;
                const value = (oldVal || '').replace(/[\r\n\t]/g, ' ');
                const newEvent = { ...e, target: { ...e.target, value } };
                input.onChange(newEvent);
                if (props.onChange) {
                    props.onChange(newEvent);
                }
            }
        }
    }

    return (
        <div className={`form-group ${props.className || ''}`}>
            <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            <div className="input-group">
                <textarea id={name} className={`form-control input-as-textarea ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''}`}
                    maxLength={maxLength} {...onInput(input)} rows={4} />
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    )
}

function SelectField(props: any) {
    const { label, meta = {}, input, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    const [options, setOptions] = useState(props.options);

    function onInput(input: any) {
        return {
            ...input,
            required,
            placeholder: props.placeholder || `Select ${label}`,
            onChange: (e: any) => {
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            isLoading: props.isLoading,
            isDisabled: props.isDisabled
        };
    }

    useEffect(() => {
        if (props.options) {
            setOptions((props.options || []).map((x: any) => {
                if (typeof x === 'string') {
                    return { value: x, label: x };
                }
                return { value: x.value || x.id, label: x.label || x.name, [props.name]: x }
            }));
        }
    }, [props.options])

    return (
        <div className={`form-group ${props.className || ''}`}>
            <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            <div className="input-group">
                <Select
                    name={name}
                    className={`select-control ${meta.touched && meta.error ? 'error-field' : ''}`}
                    options={options}
                    id={name}
                    label="label"
                    menuPosition="fixed"
                    isMulti={props.isMulti || false}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    formatOptionLabel={props.formatOptionLabel}
                    {...onInput(input)}
                />
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    )
}

function AsyncSelectField(props: any) {
    const { label, meta = {}, input, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;

    function onInput(input: any) {
        return {
            ...input,
            required,
            placeholder: props.placeholder || `Search for ${label}`,
            onChange: (e: any) => {
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            isLoading: props.isLoading,
            isDisabled: props.isDisabled,
            isClearable: props.isClearable
        };
    }


    return (
        <div className={`form-group ${props.className || ''}`}>
            <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            <div className="input-group">
                <AsyncSelect
                    name={name}
                    className={`select-control ${meta.touched && meta.error ? 'error-field' : ''}`}
                    id={name}
                    label="label"
                    menuPosition="fixed"
                    isMulti={props.isMulti || false}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    formatOptionLabel={props.formatOptionLabel}
                    loadOptions={props.loadOptions}
                    defaultOptions={props.defaultOptions}
                    {...onInput(input)}
                />
            </div>
            <span className="text-muted form-text">{props.description || `Hint: Start typing ${label} to see results.`}</span>
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    )
}

function HtmlField(props: any) {
    const { label } = useFieldApi(props);

    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm">{label}</label>
            }
            <div className="input-group">
                <div>{props.content}</div>
            </div>
        </div>
    )
}
function HtmlContentField(props: any) {
    const { label } = useFieldApi(props);

    return (
        <div className={`form-group ${props.className || ''}`}>
            <label className="form-label text-sm">{label}</label>
            <div className="input-group" dangerouslySetInnerHTML={{ __html: props.content || '--NA--' }}></div>
        </div>
    )
}

function CheckboxField(props: any) {
    const { label, input } = useFieldApi(props);

    function onInput(input: any) {
        return {
            ...input,
            type: 'checkbox',
            onChange: (e: any) => {
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            disabled: props.disabled
        }
    }
    return (
        <div className="form-check my-3">
            <input type="checkbox" id={props.name} className="form-check-input" {...onInput(input)} />
            <label title={label} htmlFor={props.name} className="form-check-label">{label}</label>
        </div>
    )

}

function TabItemField(props: any) {
    return (
        <div className={`form-group ${props.className || ''}`}
            dangerouslySetInnerHTML={{ __html: props.content || '--NA--' }}>
        </div>
    )
}

function FileUploadField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            }
            <div className={`input-group`}>
                <input id={name}
                    className={`form-control ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''} ${props.styleClass || ''}`}
                    {...input} />
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    );
};

function DocumentsWithUploadField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const [documents, setDocuments] = useState(props.documents);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;

    useEffect(() => {
        if (props.documents) {
            setDocuments(props.documents)
        }
    }, [props.documents]);
    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                documents !== undefined &&
                <>
                    {
                        (documents || []).length === 0 && <div className="fst-italic text-sm mb-2">No documents available</div>
                    }
                    {
                        (documents || []).length > 0 &&
                        <div className="d-flex flex-column">
                            {
                                (documents || []).map((document: any) => {
                                    return (
                                        <div className="d-flex flex-row w-100 align-items-center mb-2 gap-3 justify-content-between" key={document.id}>
                                            <span className="ellipse">{document.file_name}</span>
                                            <div className="d-flex flex-row align-items-center gap-3">
                                                {
                                                    props.delete && props.deleteDocument &&
                                                    <Icon action={props.deleteDocument} data={document} name="trash" className="text-error text-md" />
                                                }
                                                <Icon action={(event: any) => props.downloadDocument ? props.downloadDocument(event) : {}}
                                                    data={document} name="download" className="text-appprimary text-md" />
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                </>
            }
            {
                props.upload &&
                <>
                    {
                        label &&
                        <label className="text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
                    }
                    <div className={`input-group`}>
                        <input id={name}
                            className={`form-control ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''} ${props.styleClass || ''}`}
                            {...input} />
                    </div>
                    {
                        props.description &&
                        <span className="text-muted form-text">{props.description}</span>
                    }
                    {
                        meta.touched && meta.error && <div className="invalid-feedback d-block">
                            {meta.error}
                        </div>
                    }
                </>
            }
        </div>
    );
};

function DocumentsUploadNewField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const [documents, setDocuments] = useState(props.documents);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [validationError, setValidationError] = useState<string>('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.documents) {
            setDocuments(props.documents)
        }
    }, [props.documents]);

    const validateFiles = (files: File[]): { valid: File[], errors: string[] } => {
        const validFiles: File[] = [];
        const errors: string[] = [];
        const validators = props.validate || [];

        files.forEach(file => {
            let isValid = true;
            let fileError = '';

            // Check file type validation
            const fileTypeValidator = validators.find((v: any) => v.type === 'file-type');
            if (fileTypeValidator && fileTypeValidator.regex) {
                if (!fileTypeValidator.regex.test(file.name)) {
                    isValid = false;
                    fileError = `${file.name}: ${props.helperText || 'Invalid file type'}`;
                }
            }

            // Check file size validation
            const fileSizeValidator = validators.find((v: any) => v.type === 'file-size');
            if (fileSizeValidator && fileSizeValidator.maxSize) {
                if (file.size > fileSizeValidator.maxSize) {
                    isValid = false;
                    fileError = `${file.name}: File size exceeds ${humanReadableFileSize(fileSizeValidator.maxSize)}`;
                }
            }

            if (isValid) {
                validFiles.push(file);
            } else if (fileError) {
                errors.push(fileError);
            }
        });

        return { valid: validFiles, errors };
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const { valid, errors } = validateFiles(Array.from(files));
            if (errors.length > 0) {
                setValidationError(errors.join(', '));
            } else {
                setValidationError('');
            }
            setSelectedFiles(valid);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const { valid, errors } = validateFiles(Array.from(files));
            if (errors.length > 0) {
                setValidationError(errors.join(', '));
            } else {
                setValidationError('');
            }
            setSelectedFiles(valid);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleChooseFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
        setValidationError('');
    };

    const handleUploadFiles = () => {
        if (selectedFiles.length > 0 && props.onUpload) {
            const dataTransfer = new DataTransfer();
            selectedFiles.forEach(file => dataTransfer.items.add(file));
            props.onUpload(dataTransfer.files);
            setSelectedFiles([]);
            setValidationError('');
        }
    };

    const dropZoneClass = `d-flex flex-column align-items-center justify-content-center p-4 ${isDragging ? 'bg-pale-blue' : 'bg-gray-400'}`;
    const dropZoneBorder = `border-2 border-dashed ${isDragging ? 'border-royal-blue' : 'border-medium-gray'}`;

    return (
        <div className={`d-flex flex-column ${props.className || ''}`}>
            {
                props.upload &&
                <div className="mb-4">
                    <div className="text-lg fw-bold mb-3">Upload Document</div>

                    {/* Drop Zone */}
                    <div
                        className={`${dropZoneClass} ${dropZoneBorder}`}
                        style={{
                            borderWidth: '2px',
                            borderStyle: isDragging ? 'solid' : 'dashed',
                            borderColor: isDragging ? 'var(--royal-blue)' : 'var(--medium-gray)',
                            borderRadius: '8px',
                            minHeight: '200px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backgroundColor: isDragging ? 'var(--pale-blue)' : ''
                        }}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <Icon name="cloud-arrow-up" className="text-gray" />
                        <div className="text-dark-gray text-md mb-3 text-center">
                            Drag and drop files here or click to browse
                        </div>
                        <Button
                            variant="primary"
                            className="mb-2"
                            onClick={handleChooseFile}
                        >
                            Choose File
                        </Button>
                        <div className="text-gray text-sm text-center">{props.helperText}</div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            accept={props.accept}
                            onChange={handleFileSelect}
                            multiple={props.multiple !== undefined ? props.multiple : true}
                        />
                    </div>

                    {/* Validation Error Display */}
                    {
                        validationError &&
                        <div className="mt-2 text-error text-sm">
                            {validationError}
                        </div>
                    }

                    {/* Selected Files Display */}
                    {
                        selectedFiles.length > 0 &&
                        <div className="mt-3">
                            <div className="text-md fw-bold mb-2">Selected File{selectedFiles.length > 1 ? 's' : ''}</div>
                            <div className="d-flex flex-column gap-2">
                                {
                                    selectedFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className="d-flex flex-row align-items-center justify-content-between p-2"
                                            style={{
                                                border: '1px solid var(--medium-gray)',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--white)'
                                            }}
                                        >
                                            <span className="text-sm text-dark-gray ellipse">{file.name}</span>
                                            <Icon
                                                name="x"
                                                className="text-error text-md"
                                                style={{ cursor: 'pointer' }}
                                                action={() => handleRemoveFile(index)}
                                            />
                                        </div>
                                    ))
                                }
                            </div>
                            <Button
                                variant="primary"
                                className="w-auto mt-3"
                                onClick={handleUploadFiles}
                            >
                                Upload File{selectedFiles.length > 1 ? 's' : ''}
                            </Button>
                        </div>
                    }
                </div>
            }
            <div className="mb-4">
                <div className="text-lg fw-bold mb-3">Evidences</div>
                {
                    documents !== undefined &&
                    <>
                        {
                            (documents || []).length === 0 && <div className="fst-italic text-sm text-muted">No evidences available</div>
                        }
                        {
                            (documents || []).length > 0 &&
                            <div className="d-flex flex-column gap-2">
                                {
                                    (documents || []).map((document: any) => {
                                        return (
                                            <div
                                                className="d-flex flex-row align-items-center justify-content-between p-3"
                                                style={{
                                                    border: '1px solid var(--medium-gray)',
                                                    borderRadius: '12px',
                                                    backgroundColor: 'var(--white)',
                                                    transition: 'box-shadow 0.2s ease'
                                                }}
                                                key={document.id}
                                            >
                                                <div className="d-flex flex-column gap-2 flex-fill" style={{ minWidth: 0 }}>
                                                    <div className="d-flex flex-row align-items-center justify-content-between">
                                                        <div className="text-md text-dark-gray " 
                                                          style={{
                                                                whiteSpace: "normal",
                                                                wordBreak: "break-all",
                                                                overflowWrap: "break-word",
                                                                flexGrow: 1,
                                                                flexShrink: 1,
                                                                minWidth: 0,
                                                                paddingRight: "10px",
                                                            }}>{document.file_name}
                                                        </div>
                                                        <div className="d-flex flex-row align-items-center gap-3">
                                                            {
                                                                props.downloadDocument &&
                                                                <Icon
                                                                    action={props.downloadDocument}
                                                                    data={document}
                                                                    name="download"
                                                                    className="text-appprimary text-lg"
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            }
                                                            {
                                                                props.viewDocument &&
                                                                <Icon
                                                                    action={props.viewDocument}
                                                                    data={document}
                                                                    name="eye"
                                                                    className="text-appprimary text-lg"
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            }
                                                            {
                                                                props.delete  && props.deleteDocument &&
                                                                <Icon
                                                                    action={props.deleteDocument}
                                                                    data={document}
                                                                    name="trash"
                                                                    className="text-error text-lg"
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            }
                                                        </div>
                                                    </div>
                                                    {
                                                        document.evidence_type === EVIDENCE_TYPE.AUTOMATED &&
                                                        <span
                                                            className="text-md"
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '0.25rem 0.75rem',
                                                                backgroundColor: 'var(--medium-gray)',
                                                                color: 'var(--dark-gray)',
                                                                borderRadius: '14px',
                                                                width: 'fit-content',
                                                                fontWeight: '600'
                                                            }}
                                                        >
                                                            Auto Attached
                                                        </span>
                                                    }
                                                    {
                                                        document.status === 'archived' &&
                                                        <span
                                                            className="text-md"
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '0.25rem 0.75rem',
                                                                backgroundColor: 'var(--medium-red)',
                                                                color: 'var(--dark-red)',
                                                                borderRadius: '14px',
                                                                width: 'fit-content',
                                                                fontWeight: '600'
                                                            }}
                                                        >
                                                            Archived
                                                        </span>
                                                    }
                                                    {
                                                        document.evidence_type === EVIDENCE_TYPE.DUPLICATE &&
                                                        <span
                                                            className="text-md"
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '0.25rem 0.75rem',
                                                                backgroundColor: 'var(--medium-yellow)',
                                                                color: 'var(--dark-red)',
                                                                borderRadius: '14px',
                                                                width: 'fit-content',
                                                                fontWeight: '600'
                                                            }}
                                                        >
                                                            Duplicated
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        }
                    </>
                }
            </div>
        </div>
    );
};

export function DatePickerField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const [value, setValue] = useState(props.initialValue);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;

    function onInput(input: any) {
        return {
            ...input,
            placeholder: props.placeholder || `Select ${label}`,
            onChange: (e: any) => {
                setValue(e);
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            minDate: props.minDate,
            maxDate: props.maxDate,
            disabled: props.disabled
        }
    }

    function clearValue() {
        setValue(null);
        if (props.onChange) {
            props.onChange(null);
        }
    }

    useEffect(() => {
        if (props.initialValue) {
            setValue(props.initialValue);
        }

    }, [props.initialValue]);

    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            }
            <div className={`input-group date-picker`}>
                <DatePicker range={props.range || false}
                    format={'DD/MM/YYYY'} {...onInput(input)} value={value}>
                    {
                        value && props.clearable &&
                        <Button variant="link" className="mb-2" onClick={clearValue}>Clear</Button>
                    }
                </DatePicker>
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    );
}
export function MonthPickerField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const [value, setValue] = useState(props.initialValue);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;

    function onInput(input: any) {
        return {
            ...input,
            placeholder: props.placeholder || `Select ${label}`,
            onChange: (e: any) => {
                setValue(e);
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            disabled: props.disabled
        }
    }

    function clearValue() {
        setValue(null);
        if (props.onChange) {
            props.onChange(null);
        }
    }

    useEffect(() => {
        if (props.initialValue) {
            setValue(props.initialValue);
        }

    }, [props.initialValue])

    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            }
            <div className={`input-group month-picker`}>
                <DatePicker onlyMonthPicker={true} editable={false} range={props.range || false}
                    minDate={props.minDate} format={'MMM, YYYY'} maxDate={props.maxDate} {...onInput(input)} value={value} >
                    {
                        value && props.clearable &&
                        <Button variant="link" className="mb-2" onClick={clearValue}>Clear</Button>
                    }
                </DatePicker>
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    );
}

function TextEditorField(props: any) {
    const { input, meta, label, name } = useFieldApi(props);
    const [value, setValue] = useState(props.initialValue);
    const required = (props.validate || []).find((x: any) => x.type === validatorTypes.REQUIRED) ? true : false;
    function onInput(input: any) {
        return {
            ...input,
            placeholder: props.placeholder || `Enter ${label}`,
            onChange: (e: any) => {
                setValue(e);
                input.onChange(e);
                if (props.onChange) {
                    props.onChange(e);
                }
            },
            disabled: props.disabled
        }
    }

    return (
        <div className={`form-group ${props.className || ''}`}>
            {
                label &&
                <label className="form-label text-sm" htmlFor={name}>{label} {required && <span className="text-error">*</span>}</label>
            }
            <div className={`input-group`}>
                <ReactQuill theme="snow" className={`form-control ${meta.touched ? (meta.error ? 'is-invalid' : 'is-valid') : ''}`}
                    modules={editorConfig.modules} formats={editorConfig.formats} {...onInput(input)} value={value} id={`quillContainer_${(label || "").split(" ").join("_")}`} bounds={`#quillContainer_${(label || "").split(" ").join("_")}`} />
            </div>
            {
                props.description &&
                <span className="text-muted form-text">{props.description}</span>
            }
            {
                meta.touched && meta.error && <div className="invalid-feedback d-block">
                    {meta.error}
                </div>
            }
        </div>
    );
}

export const ComponentMapper = {
    [componentTypes.TEXT_FIELD]: TextField,
    [componentTypes.DATE_PICKER]: DatePickerField,
    [componentTypes.TEXTAREA]: TextAreaField,
    [componentTypes.SELECT]: SelectField,
    [componentTypes.PLAIN_TEXT]: HtmlField,
    [componentTypes.HTML]: HtmlContentField,
    [componentTypes.CHECKBOX]: CheckboxField,
    [componentTypes.WIZARD]: () => {
        return (<div></div>)
    },
    [componentTypes.TAB_ITEM]: TabItemField,
    [componentTypes.FILE_UPLOAD]: FileUploadField,
    [componentTypes.DOCUMENTS_UPLOAD]: DocumentsWithUploadField,
    [componentTypes.DOCUMENTS_UPLOAD_NEW]: DocumentsUploadNewField,
    [componentTypes.ASYNC_SELECT]: AsyncSelectField,
    [componentTypes.MONTH_PICKER]: MonthPickerField,
    [componentTypes.INPUT_AS_TEXT]: InputasTextAreaField,
    [componentTypes.TEXT_EDITOR]: TextEditorField
};


export function FormTemplate({ formFields }: any) {
    const { handleSubmit, onCancel, initialValues = { fullWidth: true }, getState } = useFormApi();
    const { valid, touched } = getState();

    return (
        <form onSubmit={handleSubmit} noValidate>
            {formFields}
            {
                !initialValues.hideButtons &&
                <div className={`d-flex flex-row mt-4 gap-3 ${initialValues.buttonWrapStyles || 'justify-content-center'}`}>
                    <Button variant="primary" type="submit" className={`btn btn-primary px-4 ${initialValues.fullWidth ? 'w-100' : ''}`}
                        disabled={!valid || !touched}>
                        {initialValues.submitBtnText || 'Submit'}
                    </Button>
                    {
                        initialValues.showCancel &&
                        <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={onCancel}>
                            {initialValues.cancelBtnText || 'Cancel'}
                        </Button>
                    }
                </div>
            }
        </form>
    )
}

function FormRenderer(props: any) {
    const validatorMapper = { 'file-size': fileSizeValidator, 'file-type': fileTypeValidator, ...props.customValidators };
    return (
        <>
            <DDFFormRenderer validatorMapper={validatorMapper} {...props} />
        </>
    )
}

export default FormRenderer;
